import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import Hero from "@/components/Hero";
import ContentSection from "@/components/ContentSection";
import BlogPostsSection from "@/components/BlogPostsSection";
import Footer from "@/components/Footer";
import SEO from "@/components/SEO";

const Index = () => {
  const [totalInscriptions, setTotalInscriptions] = useState(0);
  const [showCounter, setShowCounter] = useState(false);

  useEffect(() => {
    // Fetch setting to show/hide counter
    const fetchSettings = async () => {
      const { data, error } = await supabase
        .from("site_settings")
        .select("setting_value")
        .eq("setting_key", "show_inscription_counter")
        .maybeSingle();

      if (!error && data?.setting_value && typeof data.setting_value === "object" && "enabled" in data.setting_value) {
        setShowCounter(data.setting_value.enabled as boolean);
      } else {
        // If setting doesn't exist or has error, default to false (hidden)
        setShowCounter(false);
      }
    };

    // Fetch initial count using secure function
    const fetchCount = async () => {
      const { data, error } = await supabase.rpc("get_inscription_count");

      if (!error && data !== null) {
        setTotalInscriptions(data);
      }
    };

    fetchSettings();
    fetchCount();

    // Subscribe to realtime changes
    const channel = supabase
      .channel("inscriptions-changes")
      .on(
        "postgres_changes",
        {
          event: "INSERT",
          schema: "public",
          table: "inscriptions",
        },
        () => {
          // Refetch count when new inscription is added
          fetchCount();
        },
      )
      .subscribe((status, err) => {
        if (err) {
          console.debug("Realtime subscription error (non-critical):", err);
        }
      });

    return () => {
      supabase.removeChannel(channel).catch(() => {
        // Suppress channel removal errors
      });
    };
  }, []);

  const structuredData = [
    {
      "@context": "https://schema.org",
      "@type": "Organization",
      name: "Collectif Ibbü",
      alternateName: ["Collectif Ibbu", "Collectif conseillers iAdvize"],
      description: "Action collective des anciens conseillers iAdvize/Ibbu pour faire valoir leurs droits",
      url: "https://collectif-ibbu.org",
      sameAs: [
        "https://www.instagram.com/collectif.ibbu",
        "https://www.facebook.com/collectifibbu",
        "https://x.com/collectifibbu",
      ],
    },
    {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      mainEntity: [
        {
          "@type": "Question",
          name: "Qui peut rejoindre le Collectif Ibbü ?",
          acceptedAnswer: {
            "@type": "Answer",
            text: "Le Collectif Ibbü est ouvert à tous les conseillers ayant travaillé ou travaillant encore avec la plateforme iAdvize/Ibbu. Que vous ayez été conseiller récemment ou il y a plusieurs années, si vous souhaiteriez faire valoir vos droits, vous pouvez nous rejoindre. L'inscription est gratuite et confidentielle.",
          },
        },
        {
          "@type": "Question",
          name: "Qu'est-ce que la plateforme iAdvize/Ibbü ?",
          acceptedAnswer: {
            "@type": "Answer",
            text: "iAdvize est une plateforme de messaging conversationnel qui mettrait en relation des conseillers (anciennement via Ibbu) avec des marques pour répondre aux questions de leurs clients. Les conseillers, considérés comme indépendants, auraient pu rencontrer diverses problématiques liées à leur statut et leurs conditions de collaboration avec la plateforme.",
          },
        },
        {
          "@type": "Question",
          name: "Quels seraient les problèmes rencontrés par les conseillers iAdvize/Ibbu ?",
          acceptedAnswer: {
            "@type": "Answer",
            text: "Selon des témoignages de conseillers, plusieurs problématiques pourraient avoir été rencontrées : une baisse importante du volume de missions sans explication claire, des ruptures soudaines de collaboration, des décisions techniques impactant directement les revenus, et des questions sur le statut juridique réel de leur relation avec la plateforme. Ces situations soulèveraient des interrogations sur une éventuelle qualification en salariat.",
          },
        },
        {
          "@type": "Question",
          name: "Comment les conseillers Ibbü étaient-ils rémunérés ?",
          acceptedAnswer: {
            "@type": "Answer",
            text: "Les conseillers de la plateforme iAdvize/Ibbu auraient été rémunérés selon différents modèles qui pourraient avoir évolué au fil du temps : rémunération à la conversation, au temps passé connecté, ou à la mission. Certains conseillers auraient constaté des modifications unilatérales de ces conditions de rémunération, ce qui pourrait soulever des questions juridiques sur la nature de la relation contractuelle.",
          },
        },
        {
          "@type": "Question",
          name: "Quels sont les objectifs du Collectif Ibbü face à iAdvize ?",
          acceptedAnswer: {
            "@type": "Answer",
            text: "Le Collectif Ibbü vise à rassembler les témoignages des conseillers pour constituer un dossier factuel, à s'informer mutuellement sur les droits potentiels, et à organiser une démarche juridique collective si nécessaire. L'objectif serait de clarifier la nature de la relation entre les conseillers et iAdvize/Ibbu, et de faire reconnaître les droits qui pourraient découler d'un éventuel statut salarié. Notre démarche se veut respectueuse du droit et basée sur des faits.",
          },
        },
        {
          "@type": "Question",
          name: "Est-ce que rejoindre le Collectif Ibbü comporte des risques vis-à-vis d'iAdvize ?",
          acceptedAnswer: {
            "@type": "Answer",
            text: "Rejoindre un collectif pour faire valoir ses droits est un droit fondamental garanti par la loi. Vos données personnelles sont traitées de manière confidentielle et ne seront communiquées à des tiers (y compris à un avocat) qu'avec votre consentement explicite. Le collectif agit dans le respect du droit et ne pratique aucune forme de diffamation envers iAdvize ou Ibbu.",
          },
        },
        {
          "@type": "Question",
          name: "Quelle est la différence entre Ibbu (sans tréma) et Ibbü (avec tréma) ?",
          acceptedAnswer: {
            "@type": "Answer",
            text: 'Ibbu et Ibbü désignent exactement la même plateforme. L\'orthographe avec tréma ("Ibbü") est simplement une variante graphique, perçue comme plus récente ou plus distinctive. En pratique, beaucoup écrivent "Ibbu" sans tréma, car c\'est plus simple à saisir au clavier. Ces deux formes renvoient au même service de mise en relation entre conseillers et marques, via la plateforme iAdvize. Notre collectif utilise l\'orthographe "Ibbü", mais représente l\'ensemble des conseillers, quelle que soit la période ou l\'orthographe utilisée.',
          },
        },
      ],
    },
  ];

  return (
    <>
      <SEO
        title="Collectif Ibbü | Action des conseillers iAdvize/Ibbu"
        description="Collectif Ibbü : rejoignez l'action collective des anciens conseillers iAdvize/Ibbu pour faire valoir nos droits. Démarche juridique groupée, témoignages et solidarité."
        canonical="https://collectif-ibbu.org"
        structuredData={structuredData}
      />
      <div className="min-h-screen">
        <main>
          <Hero totalInscriptions={totalInscriptions} showCounter={showCounter} />
          <ContentSection />
          <BlogPostsSection />
        </main>
        <Footer />
      </div>
    </>
  );
};

export default Index;
